import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import Autocomplete from "@/components/Autocomplete";
import Breadcrumb from "@/components/Breadcrumb";
import EmptyState from "@/components/EmptyState";
import PageHeading from "@/components/PageHeading/PageHeading";
import Table, { TableControls, TableFooter, TableHeading } from "@/components/Table";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import usePageTitle from "@/hooks/usePageTitle";
import usePagination from "@/hooks/usePagination";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useParams } from "@/hooks/useSearchParams";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import AppLayout from "@/layouts/AppLayout";
import {
  ACHIEVEMENT_TYPES_QUERY,
  type AchievementTypesQueryResponse,
  type MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import { useMembershipServicesNavLinks } from "@/pages/people/_membershipServicesNavLinks";
import type { Path } from "@/router";
import type { Section } from "@/types";
import ExpandedAchievementFields from "./_components/ExpandedAchievementFields";
import {
  buildSectionLookup,
  findDefaultAchievementType,
  hasNestedAchievementFields,
  mapAchievementReportRows
} from "./_utils";
import {
  ACHIEVEMENTS_FOR_MEMBERS_QUERY,
  type AchievementForMembersOrderByInput,
  type AchievementsForMembersQueryResponse,
  type AchievementsForMembersQueryVariables
} from "./queries";
import { ACHIEVEMENT_REPORT_SORT_FIELD_MAP, getColumns } from "./_TableColumns";

type AchievementTypeOption = {
  label: string;
  value: string;
};

function buildAchievementTypeOptions(achievementTypes: MembershipAchievementType[] = []): AchievementTypeOption[] {
  return achievementTypes.map((achievementType) => ({
    label: achievementType.title,
    value: achievementType.id
  }));
}

export default function AchievementsReportPage() {
  usePageTitle("/people/membership-services/achievements-report", "People | Achievements Report");
  const navLinks = useMembershipServicesNavLinks();
  const { isCompactLayout } = useIsCompactLayout();
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const achievementId = typeof params.achievementId === "string" ? params.achievementId : undefined;
  const { Menu, setMenu } = useTableRowMenu();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [achievementTypeQuery, setAchievementTypeQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearchTerm(achievementTypeQuery.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [achievementTypeQuery]);

  useEffect(() => {
    resetPagination();
  }, [achievementId, resetPagination]);

  const {
    data: achievementTypesData,
    loading: achievementTypesLoading,
    error: achievementTypesError
  } = useQuery<AchievementTypesQueryResponse>(ACHIEVEMENT_TYPES_QUERY, {
    variables: { search: debouncedSearchTerm || null },
    fetchPolicy: "cache-first"
  });

  const { data: sectionsData } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    fetchPolicy: "cache-first"
  });

  const achievementTypeOptions = useMemo(
    () => buildAchievementTypeOptions(achievementTypesData?.achievementTypes),
    [achievementTypesData?.achievementTypes]
  );

  const defaultAchievementType = useMemo(
    () => findDefaultAchievementType(achievementTypesData?.achievementTypes),
    [achievementTypesData?.achievementTypes]
  );
  const hasAppliedDefaultType = useRef(!!achievementId);

  useEffect(() => {
    if (achievementId || hasAppliedDefaultType.current || !defaultAchievementType) {
      return;
    }

    hasAppliedDefaultType.current = true;
    addSearchParam("achievementId", defaultAchievementType.id);
    setAchievementTypeQuery("");
    setDebouncedSearchTerm("");
  }, [achievementId, addSearchParam, defaultAchievementType]);

  const selectedAchievementType = useMemo(
    () => achievementTypeOptions.find((option) => option.value === achievementId),
    [achievementId, achievementTypeOptions]
  );

  const orderBy = useMemo((): AchievementForMembersOrderByInput | undefined => {
    const sort = sorting[0];
    if (!sort) return undefined;

    const field = ACHIEVEMENT_REPORT_SORT_FIELD_MAP[sort.id];
    if (!field) return undefined;

    return {
      field,
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [sorting]);

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
    resetPagination();
  };

  const {
    data: currentData,
    previousData,
    loading: achievementsLoading,
    error: achievementsError
  } = useQuery<AchievementsForMembersQueryResponse, AchievementsForMembersQueryVariables>(
    ACHIEVEMENTS_FOR_MEMBERS_QUERY,
    {
      variables: {
        achievementId: achievementId!,
        ...paginationVariables,
        ...(orderBy ? { orderBy } : {})
      },
      skip: !achievementId
    }
  );

  const data = achievementId && !achievementsError ? currentData || previousData : undefined;
  const connection = data?.achievementsForMembers;
  const tableData = useMemo(() => mapAchievementReportRows(connection?.nodes ?? []), [connection?.nodes]);
  const totalRowCount = connection?.totalCount ?? tableData.length;
  const sectionLookup = useMemo(() => buildSectionLookup(sectionsData?.sections), [sectionsData?.sections]);

  const columns = useMemo(
    () =>
      getColumns({
        isCompactLayout,
        onViewPerson: (pgaId) => {
          setSelectedPgaId(pgaId);
          setIsPersonSideSheetOpen(true);
        },
        setMenu
      }),
    [isCompactLayout, setMenu]
  );

  const hasAchievementTypeSelected = !!achievementId;
  const hasActiveFilters = !achievementTypesError;
  const achievementTypeFilter = (
    <Autocomplete<AchievementTypeOption>
      variant="searchableDropdown"
      label="Achievement Type"
      required
      data={achievementTypeOptions}
      query={selectedAchievementType?.label ?? achievementTypeQuery}
      loadingResults={achievementTypesLoading}
      placeholder="Search achievement types"
      data-testid="achievement-type-filter"
      leadingIcon="search"
      constrainMenuWidth
      menuClassName="max-h-[300px]"
      renderItem={(option) => (
        <div className="flex flex-col">
          <div className="text-body-large">{option.label}</div>
        </div>
      )}
      onChangeText={(text) => {
        setAchievementTypeQuery(text);
        if (achievementId) {
          deleteSearchParams("achievementId");
        }
      }}
      reset={() => {
        setAchievementTypeQuery("");
        setDebouncedSearchTerm("");
        deleteSearchParams("achievementId");
      }}
      onSelect={(option) => {
        addSearchParam("achievementId", option.value);
        setAchievementTypeQuery("");
        setDebouncedSearchTerm("");
      }}
    />
  );

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="Achievements Report"
        backLink={{ to: "/people/membership-services" }}
        compact
      />
      <PageHeading title="Achievements Report" compact />
      <div className="w-full">
        <TableHeading heading="Achievements Report" body="View a list of awards and certifications earned by people" />

        <Table
          data={tableData}
          columns={columns}
          loading={achievementsLoading && hasAchievementTypeSelected}
          hasActiveFilters={hasActiveFilters}
          serverSideSorting
          sortingState={sorting}
          onSortingChange={handleSortingChange}
          getRowLink={(row) => (row.original.pgaId ? { to: `/people/${row.original.pgaId}` as Path } : null)}
          getRowCanExpand={(row) => hasNestedAchievementFields(row.original)}
          renderExpandedRow={({ row }) => (
            <ExpandedAchievementFields row={row.original} sectionLookup={sectionLookup} />
          )}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              pagination={createHeaderPagination(connection, {
                loading: achievementsLoading,
                pageCount: tableData.length,
                totalCount: totalRowCount
              })}
              onSortClick={onSortClick}
              useExpandedFilters={false}
            >
              <div data-name="achievementId" data-filter-size="custom" className="min-w-[280px]">
                {achievementTypeFilter}
              </div>
            </TableControls>
          )}
          renderFooter={() => (
            <TableFooter
              pagination={createFooterPagination(connection, {
                loading: achievementsLoading,
                pageCount: tableData.length,
                totalCount: totalRowCount
              })}
              totalRowCount={totalRowCount}
            />
          )}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="Unable to load achievement types"
              caption={achievementTypesError?.message ?? "Try again later."}
              iconName="error"
            />
          )}
          renderFilteredEmptyState={() =>
            achievementsError ? (
              <EmptyState
                title="Unable to load recognized members"
                caption={achievementsError.message}
                iconName="error"
              />
            ) : hasAchievementTypeSelected ? (
              <EmptyState
                title="No Members Recognized"
                caption="There are no members recognized for this achievement."
                iconName="emoji_events"
              />
            ) : (
              <EmptyState
                title="Select an Achievement Type"
                caption="Choose an achievement type to view recognized members."
                iconName="emoji_events"
              />
            )
          }
          data-testid="achievements-report-table"
        />
      </div>

      <Menu />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
        title="Member Details"
      />
    </AppLayout>
  );
}
